import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import ImageCropper, { Crop, makeAspectCrop } from 'react-image-crop';

import { Button } from '../../../button';
import Modal from '../../../modal';
import { FileInput } from '../file';
import FileInputPreview from './file-input-preview';

import { Attachment, FileInProgress, FileInputValue } from '@common/types/objects';
import { getCurrentOrgId } from '@modules/organisation/selectors/organisation';
import { useAppSelector } from '@common/hooks';
import { setFormFileAttachments } from '../file/utils';

const DEFAULT_CROP = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
  unit: 'px' as 'px' | '%',
};

export type FileCropInputProps = PropsWithChildren<{
  value?: Attachment | null;
  aspectRatio?: number;
  submitButtonText?: string;
  preview?: boolean;
  unsplash?: boolean;
  onSubmit?: ((value?: FileInputValue | null, index?: number) => void);
  onChange?: ((value?: FileInputValue | null, index?: number) => void);
  processFile?: boolean;
  acceptedExtLabel?: string;
}>;

const FileCropInput = ({
  aspectRatio = 1, submitButtonText, preview, children, unsplash, value,
  onChange, onSubmit, processFile, acceptedExtLabel,
}: FileCropInputProps) => {
  const { t } = useTranslation();
  const organisationId = useAppSelector(getCurrentOrgId);
  const pendingUploadCancelRef = useRef<(() => void) | null>(null);
  const isCropModalOpenRef = useRef(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<FileInputValue | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>({ aspect: aspectRatio, ...DEFAULT_CROP });

  useLayoutEffect(() => {
    isCropModalOpenRef.current = isVisibleModal;
  }, [isVisibleModal]);

  const handleImageLoad = (newImage: HTMLImageElement) => {
    const imageRatio = newImage.width / newImage.height; // Aspect ratio for the selected image
    const isImageHorizontal = imageRatio > aspectRatio;
    const maxSize = isImageHorizontal ? newImage.height : newImage.width; // Select what the max size of the image can be
    const size = {
      width: isImageHorizontal ? maxSize * aspectRatio : maxSize,
      height: !isImageHorizontal ? maxSize * aspectRatio : maxSize,
    };

    const newCrop = makeAspectCrop({
      ...DEFAULT_CROP,
      aspect: aspectRatio,
      width: size.width,
      height: size.height,
    }, newImage.width, newImage.height);

    // Center the crop selection in the center
    if (newImage.width > newCrop.width) {
      newCrop.x = (newImage.width - newCrop.width) / 2;
    } else if (newImage.height > newCrop.height) {
      newCrop.y = (newImage.height - newCrop.height) / 2;
    }

    setCrop(newCrop);
    setImage(newImage);
  };

  const cancelPendingUpload = useCallback(() => {
    pendingUploadCancelRef.current?.();
    pendingUploadCancelRef.current = null;
  }, []);

  useEffect(() => () => {
    pendingUploadCancelRef.current?.();
    pendingUploadCancelRef.current = null;
  }, []);

  const handleProcess = () => {
    if (!image || !crop) return;

    cancelPendingUpload();
    pendingUploadCancelRef.current = null;

    const pixelRatio = window.devicePixelRatio;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement('canvas');
    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    const ctx = canvas.getContext('2d');
    ctx!.drawImage(
      image,
      crop.x! * scaleX,
      crop.y! * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0, 0, canvas.width, canvas.height,
    );

    setLoading(true);
    canvas.toBlob((blob) => {
      if (blob) {
        const origOnChange = onChange || onSubmit;
        setFormFileAttachments({
          files: [blob as File],
          organisationId,
          onChange: (uploadedFile?: FileInputValue | null, fileIndex?: number) => {
            if (
              uploadedFile
              && typeof uploadedFile === 'object'
              && 'cancelPromise' in uploadedFile
              && typeof (uploadedFile as FileInProgress).cancelPromise === 'function'
            ) {
              pendingUploadCancelRef.current = () => (
                void (uploadedFile as FileInProgress).cancelPromise?.()
              );
            }

            if (origOnChange) origOnChange(uploadedFile, fileIndex);
            if (uploadedFile === undefined) {
              pendingUploadCancelRef.current = null;
              setLoading(false);
              return;
            }

            if (!processFile) {
              setIsVisibleModal(false);
              setFile(null);
              setImage(null);
              setCrop({ aspect: aspectRatio, ...DEFAULT_CROP });
              setLoading(false);
              return;
            }

            // When upload completes, we receive an Attachment containing a real `path`.
            const isUploadComplete = uploadedFile
              && typeof uploadedFile === 'object'
              && 'path' in uploadedFile
              && typeof (uploadedFile as Attachment).path === 'string'
              // @ts-expect-error
              && (uploadedFile as Attachment).path.length > 0;

            if (isUploadComplete) {
              pendingUploadCancelRef.current = null;
              setIsVisibleModal(false);
              setFile(null);
              setImage(null);
              setCrop({ aspect: aspectRatio, ...DEFAULT_CROP });
              setLoading(false);
            }
          },
          options: { processFile },
        });
      } else {
        setLoading(false);
      }
    }, 'image/jpeg');
  };

  const handlePickFile = (newFile?: FileInputValue) => {
    if (!newFile) {
      setFile(null);
      return;
    }

    const attachmentLikeType = typeof newFile === 'object' && newFile !== null && 'type' in newFile
      ? (newFile as Attachment).type
      : null;
    const isCommittedServerAttachment =
      attachmentLikeType === 'attachment'
      || attachmentLikeType === 'other_attachment';

    /**
     * `FileInput` with processFile=true uploads immediately; when that completion
     * arrives after this dialog closed (or superseded), it must not re-open crop UI.
     * Ref avoids stale closures from async upload callbacks vs `isVisibleModal` state.
     */
    if (isCommittedServerAttachment && !isCropModalOpenRef.current) return;

    setFile(newFile);
    setIsVisibleModal(true);
  };

  const fileInput = (
    <FileInput
      accept="image/jpg,image/jpeg,image/png,image/gif"
      processFile={false}
      unsplash={unsplash}
      onChange={handlePickFile}
      acceptedExtLabel={acceptedExtLabel}
    />
  );

  const trigger = useMemo(() => {
    if (Array.isArray(children) && typeof children[0] === 'string') return <a>{children[0]}</a>;
    if (!children && (!preview || !value)) return fileInput;
    return children;
  }, [children, preview, value]);

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsVisibleModal(open);
    if (!open) {
      cancelPendingUpload();
      setFile(null);
      setImage(null);
      setCrop({ aspect: aspectRatio, ...DEFAULT_CROP });
      setLoading(false);
    }
  }, [aspectRatio, cancelPendingUpload]);

  return (
    <>
      <Modal
        show={isVisibleModal}
        onOpenChange={handleOpenChange}
        className="Form"
        title={t('common:form_input_image_choose_file')}
        onExited={() => {
          setFile(null);
          setImage(null);
          setCrop({ aspect: aspectRatio, ...DEFAULT_CROP });
          setLoading(false);
        }}
        onClose={() => setIsVisibleModal(false)}
        content={file ? (
          <div className="tw-text-center">
            <ImageCropper
              keepSelection
              crop={crop}
              src={`${file.preview}`}
              onImageLoaded={handleImageLoad}
              onChange={(newCrop) => {
                if (newCrop.width === 0) return;
                setCrop(newCrop);
              }}
            />
          </div>
        ) : fileInput}
        footerChildren={(
          <Button
            type="primary"
            isLoading={loading}
            onPointerDown={(e: React.PointerEvent) => {
              // Prevent "click-through" when the modal closes between
              // pointer down/up, which can re-trigger the underlying
              // file input and reopen this modal.
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleProcess();
            }}
          >
            {submitButtonText || (
              preview ?
                t('common:form_input_image_save') :
                t('common:form_input_image_upload')
            )}
          </Button>
        )}
      >
        {trigger}
      </Modal>
      {preview && value && value.path && (
        <FileInputPreview
          value={value}
          handleClear={() => onChange && onChange(null)}
          handlePickFile={handlePickFile}
        />
      )}
    </>
  );
};

export default FileCropInput;
